import React, { useMemo } from "react";
import { getLeadingAccountConnection } from "@common/helpers/financialAccount";
import { getConnectionLabel } from "@common/helpers/financialConnection";
import { getAnonymousAccountNumber } from "@common/helpers/bankAccount";
import {
    FinancialAccountType,
    type FinancialAccount,
} from "@common/types/financialAccount";
import type { Subaccount } from "@common/types/subaccount";
import {
    getIntegrationConnectionInstitutionName,
    isIntegrationConnection,
} from "@common/helpers/integrationConnection";
import { getSubaccountDisplayName } from "@design-system/Subaccount/utils";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { HStack } from "@design-system/HStack/HStack";
import { AvatarGroup } from "@design-system/Avatar/AvatarGroup";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { AccountPickerIcon } from "@components/financialAccount/AccountPickerItem";

interface Props {
    isAccountSelected: (accountId: number, entityId: number) => boolean;
    isSubaccountSelected?: (subaccountId: string) => boolean;
    showSelectedIcons: boolean;
    isMultiple?: boolean;
}

const ICONS_LIMIT = 4;

/** The name the filter shows for an account; cash accounts add their entity. */
const getAccountDisplayName = (
    account: Pick<FinancialAccount, "financialType" | "name" | "entity">,
) =>
    account.financialType === FinancialAccountType.CASH
        ? `${account.name} - ${account.entity?.name}`
        : account.name;

/** The institution or connection label an account's logo stands for. */
const getConnectionName = (account: FinancialAccount) => {
    const connection = getLeadingAccountConnection(account);
    if (!connection) {
        return null;
    }
    return isIntegrationConnection(connection)
        ? getIntegrationConnectionInstitutionName(connection)
        : getConnectionLabel(connection);
};

interface ConnectionLogo {
    /** The first selected account of the connection; its logo is shown. */
    account: FinancialAccount;
    /** The connection name, or the account names when it has none. */
    label: string;
}

/**
 * One logo per connection, in selection order. Accounts without a connection
 * share one logo, labelled with all their names.
 */
const toConnectionLogos = (
    accounts: readonly FinancialAccount[],
): ConnectionLogo[] => {
    const groups = new Map<string | null, FinancialAccount[]>();
    for (const account of accounts) {
        const key = getConnectionName(account);
        groups.set(key, [...(groups.get(key) ?? []), account]);
    }
    return Array.from(groups, ([name, groupAccounts]) => ({
        account: groupAccounts[0],
        label: name ?? groupAccounts.map(getAccountDisplayName).join(", "),
    }));
};

/** The empty-state label, the one subaccount's name, or the subaccount count. */
const getSubaccountsValue = ({
    subaccounts,
    emptyLabel,
}: {
    subaccounts: readonly Subaccount[];
    emptyLabel: string;
}) => {
    if (subaccounts.length === 0) {
        return emptyLabel;
    }
    if (subaccounts.length === 1) {
        return getSubaccountDisplayName(subaccounts[0]);
    }
    return <>{subaccounts.length} subaccounts</>;
};

/** The masked number, else the display name. */
const SelectedAccountName = ({ account }: { account: FinancialAccount }) => (
    <span>
        {account.accountNumberMask ? (
            <span>
                {getAnonymousAccountNumber(account.accountNumberMask, 4)}
            </span>
        ) : (
            <BreakableText text={getAccountDisplayName(account)} />
        )}
    </span>
);

export const AccountsFilterAccountValueDisplay: React.FC<Props> = ({
    isAccountSelected,
    isSubaccountSelected,
    showSelectedIcons,
    isMultiple,
}) => {
    const accounts = useFinancialAccounts({ createTempCashAccounts: true });
    const selectedAccounts = useMemo(
        () =>
            accounts.filter(
                (account) =>
                    account.entityId &&
                    isAccountSelected(account.id, account.entityId),
            ),
        [accounts, isAccountSelected],
    );

    const selectedSubaccounts = useMemo(
        () =>
            accounts
                .flatMap((account) => account.subaccounts ?? [])
                .filter((subaccount) => isSubaccountSelected?.(subaccount.id)),
        [accounts, isSubaccountSelected],
    );

    if (selectedAccounts.length === 0) {
        return getSubaccountsValue({
            subaccounts: selectedSubaccounts,
            emptyLabel: isMultiple ? "Select accounts" : "Select account",
        });
    }

    if (selectedAccounts.length === 1) {
        const [account] = selectedAccounts;
        return (
            <HStack gap={8} align="center">
                {showSelectedIcons && <AccountPickerIcon account={account} />}
                <SelectedAccountName account={account} />
            </HStack>
        );
    }

    return (
        <>
            {showSelectedIcons && (
                <AvatarGroup
                    items={toConnectionLogos(selectedAccounts)}
                    getKey={({ account }) => account.id}
                    getLabel={({ label }) => label}
                    renderAvatar={({ account }) => (
                        <AccountPickerIcon account={account} />
                    )}
                    max={ICONS_LIMIT}
                    size="xs"
                />
            )}
            <span>{selectedAccounts.length} accounts</span>
        </>
    );
};
